import { Button, Descriptions, Tag } from 'antd';
import { FileTextOutlined, UserOutlined, VideoCameraOutlined } from '@ant-design/icons';
import { Link } from '@tanstack/react-router';
import type {
  AppointmentDetailSection,
  ExtendedModuleAppointmentExtension,
} from '@/components/appointments/extensions/extension-types';
import type { Appointment } from '@/api/mcp-appointments';
import { getMeetingInfo } from '@/components/appointments/extensions/extension-utils';
import { MODULE_BASE_ROUTE } from '../index';

/** Names of the appointment context keys that hold the linked application's data. */
export interface ApplicationEventFieldMap {
  /** Detail section id shown in the appointment popup. */
  sectionId: string;
  /** Context key with the job title / position. */
  title: string;
  /** Context key with the candidate name. */
  name: string;
  /** Context key with the candidate email. */
  email: string;
}

type ApplicationEventHelpers = Required<
  Pick<
    ExtendedModuleAppointmentExtension,
    'getEventIcon' | 'getEventLabel' | 'getEventTooltip' | 'getDetailSections'
  >
>;

/**
 * Builds the calendar display methods (icon, label, tooltip, detail section)
 * for appointments linked to an application, reading the application data
 * from the appointment context keys named in `fieldMap`.
 */
export function buildApplicationEventHelpers(fieldMap: ApplicationEventFieldMap): ApplicationEventHelpers {
  return {
    getEventIcon: (appointment: Appointment, contextData: Record<string, any>) => {
      // Empty context means this is not a module appointment: use the default icon
      if (!contextData || Object.keys(contextData).length === 0) return undefined;
      const meeting = getMeetingInfo(appointment);
      return meeting.format === 'VIDEO'
        ? <VideoCameraOutlined style={{ fontSize: '13px' }} />
        : <UserOutlined style={{ fontSize: '13px' }} />;
    },

    getEventLabel: (appointment: Appointment, contextData: Record<string, any>) => {
      if (contextData[fieldMap.title]) return `Interview: ${contextData[fieldMap.title]}`;
      return contextData[fieldMap.name] || appointment.anonymousName || `Resource ${appointment.resourceId}`;
    },

    getEventTooltip: (appointment: Appointment, contextData: Record<string, any>) => {
      const meeting = getMeetingInfo(appointment);
      return (
        <div style={{ padding: '4px 0' }}>
          {contextData[fieldMap.title] && (
            <div><strong>Position:</strong> {contextData[fieldMap.title]}</div>
          )}
          <div><strong>Format:</strong> {meeting.format === 'VIDEO' ? 'Video Interview' : 'In-Person Interview'}</div>
          {contextData[fieldMap.name] && (
            <div><strong>Candidate:</strong> {contextData[fieldMap.name]}</div>
          )}
          {meeting.meetingModeratorUrl && (
            <div><strong>Owner Link:</strong> Available</div>
          )}
          {meeting.meetingUrl && (
            <div><strong>Member Link:</strong> Available</div>
          )}
        </div>
      );
    },

    getDetailSections: (_appointment: Appointment, contextData: Record<string, any>): AppointmentDetailSection[] => {
      // Application linkage only; meeting fields are shown by the shared details
      if (!contextData[fieldMap.title] && !contextData.applicationId) return [];

      return [
        {
          id: fieldMap.sectionId,
          title: 'Interview Information',
          position: 'after',
          order: 1,
          content: (
            <div>
              <Descriptions column={1} size="small" bordered={false}>
                {contextData[fieldMap.title] && (
                  <Descriptions.Item label="Position">
                    <Tag color="blue">{contextData[fieldMap.title]}</Tag>
                  </Descriptions.Item>
                )}
                {contextData[fieldMap.name] && (
                  <Descriptions.Item label="Candidate">{contextData[fieldMap.name]}</Descriptions.Item>
                )}
                {contextData[fieldMap.email] && (
                  <Descriptions.Item label="Email">{contextData[fieldMap.email]}</Descriptions.Item>
                )}
              </Descriptions>
              <Link to={`${MODULE_BASE_ROUTE}/applications/${contextData.applicationId}/summary`}>
                <Button type="primary" icon={<FileTextOutlined />} style={{ marginTop: '10px' }}>
                  View application summary
                </Button>
              </Link>
            </div>
          ),
        },
      ];
    },
  };
}
